import React, { useEffect } from 'react';
import * as d3 from 'd3';
import { useChartDimensions } from '../hooks/useChartDimensions';

const TRACK_COLORS = {
  GR: '#E8A317',
  RHOB: '#DC2626',
  NPHI: '#2563EB',
  RT: '#7C3AED',
  DT: '#0D9488',
  SP: '#64748B',
  CALI: '#B45309',
};

/**
 * Simple multi-track LAS log plot (depth vs preferred curves).
 * data: { depth: number[], curves: { GR: (number|null)[], ... } }
 */
export default function LasLogChart({ data, heightRatio = 0.9 }) {
  const { containerRef, svgRef, tooltipRef, dimensions, hideTooltip } = useChartDimensions(
    (w) => Math.min(640, Math.max(420, w * heightRatio)),
  );

  useEffect(() => {
    if (!data?.depth?.length || !dimensions.width) {
      hideTooltip();
      return;
    }
    const curveKeys = Object.keys(data.curves || {}).filter((k) =>
      (data.curves[k] || []).some((v) => v != null),
    );
    if (!curveKeys.length) {
      hideTooltip();
      return;
    }

    const { width, height } = dimensions;
    const margin = { top: 28, right: 12, bottom: 28, left: 56 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const trackGap = 8;
    const trackW = (innerW - trackGap * (curveKeys.length - 1)) / curveKeys.length;

    const depthVals = data.depth.filter((d) => d != null);
    const y = d3
      .scaleLinear()
      .domain([d3.min(depthVals), d3.max(depthVals)])
      .range([0, innerH]);

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${width} ${height}`);
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    g.append('g')
      .call(d3.axisLeft(y).ticks(8).tickFormat((d) => `${d}`))
      .selectAll('text')
      .attr('font-size', 10)
      .attr('fill', '#64748B');
    g.append('text')
      .attr('x', -40)
      .attr('y', -10)
      .attr('font-size', 10)
      .attr('fill', '#64748B')
      .text('Depth (ft)');

    curveKeys.forEach((key, i) => {
      const x0 = i * (trackW + trackGap);
      const vals = data.curves[key].filter((v) => v != null);
      let xMin = d3.min(vals);
      let xMax = d3.max(vals);
      if (key === 'GR') {
        xMin = 0;
        xMax = Math.max(xMax, 150);
      }
      if (key === 'NPHI') {
        xMin = Math.min(xMin, -0.05);
        xMax = Math.max(xMax, 0.45);
      }
      if (key === 'RT') {
        // log scale feel via domain
        xMin = Math.max(0.2, xMin);
        xMax = Math.max(xMax, 100);
      }
      const x =
        key === 'RT'
          ? d3.scaleLog().domain([xMin, xMax]).range([0, trackW]).clamp(true)
          : d3.scaleLinear().domain([xMin, xMax]).range([0, trackW]).nice();

      const tg = g.append('g').attr('transform', `translate(${x0},0)`);
      tg.append('rect')
        .attr('width', trackW)
        .attr('height', innerH)
        .attr('fill', '#F8FAFC')
        .attr('stroke', '#E2E8F0');

      tg.append('g')
        .attr('transform', `translate(0,${innerH})`)
        .call(d3.axisBottom(x).ticks(3).tickSize(3))
        .selectAll('text')
        .attr('font-size', 9)
        .attr('fill', '#64748B');

      tg.append('text')
        .attr('x', trackW / 2)
        .attr('y', -8)
        .attr('text-anchor', 'middle')
        .attr('font-size', 11)
        .attr('font-weight', 700)
        .attr('fill', TRACK_COLORS[key] || '#0C1F2E')
        .text(key);

      const pts = [];
      for (let r = 0; r < data.depth.length; r += 1) {
        const d = data.depth[r];
        const v = data.curves[key][r];
        if (d == null || v == null) continue;
        if (key === 'RT' && v <= 0) continue;
        pts.push([x(v), y(d)]);
      }
      if (pts.length < 2) return;
      const line = d3.line().x((p) => p[0]).y((p) => p[1]).curve(d3.curveMonotoneY);
      tg.append('path')
        .attr('d', line(pts))
        .attr('fill', 'none')
        .attr('stroke', TRACK_COLORS[key] || '#0C1F2E')
        .attr('stroke-width', 1.4);
    });

    const tip = d3.select(tooltipRef.current);
    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .on('mousemove', (event) => {
        const [, my] = d3.pointer(event);
        const depth = y.invert(my);
        // nearest sample
        let best = 0;
        let bestDist = Infinity;
        data.depth.forEach((d, i) => {
          if (d == null) return;
          const dist = Math.abs(d - depth);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        const parts = [`${data.depth[best]?.toFixed?.(1) ?? '—'} ft`];
        curveKeys.forEach((k) => {
          const v = data.curves[k][best];
          if (v != null) parts.push(`${k}: ${Number(v).toPrecision(4)}`);
        });
        tip
          .style('opacity', 1)
          .style('left', `${event.offsetX + 12}px`)
          .style('top', `${event.offsetY - 10}px`)
          .html(parts.join('<br/>'));
      })
      .on('mouseleave', hideTooltip);
    return hideTooltip;
  }, [data, dimensions, svgRef, tooltipRef, hideTooltip]);

  return (
    <div className="chart-container" ref={containerRef}>
      <svg ref={svgRef} />
      <div className="chart-tooltip" ref={tooltipRef} />
    </div>
  );
}
